import { useState, useEffect } from 'react'
import { supabase } from '@/config/supabase'
import { useUIStore } from '@/stores/ui.store'
import {
    Building2, CreditCard, Bell, Shield, Palette, Save,
    Globe, Mail, Phone, MapPin, FileText, Zap, CheckCircle2,
    Percent, FileType, Loader2
} from 'lucide-react'

/* ── Seções ────────────────────────────────────────────── */

interface Section {
    id: string
    icon: React.ElementType
    label: string
}

const sections: Section[] = [
    { id: 'empresa', icon: Building2, label: 'Dados da Empresa' },
    { id: 'cobranca', icon: CreditCard, label: 'Cobrança e Pagamentos' },
    { id: 'taxas', icon: Percent, label: 'Taxas de Cobrança' },
    { id: 'templates', icon: FileType, label: 'Templates de Descrição' },
    { id: 'notificacoes', icon: Bell, label: 'Notificações' },
    { id: 'aparencia', icon: Palette, label: 'Aparência' },
    { id: 'seguranca', icon: Shield, label: 'Segurança' },
]

/* ── State inicial ─────────────────────────────── */

const INITIAL_SETTINGS = {
    /* empresa */
    nome_empresa: 'Minha Empresa de Proteção',
    cnpj: '',
    email_empresa: '',
    telefone_empresa: '',
    endereco: '',
    site: '',
    /* cobrança */
    asaas_api_key: '',
    asaas_ambiente: 'sandbox' as 'sandbox' | 'producao',
    dia_vencimento_padrao: 10,
    multa_atraso: 2,
    juros_mes: 1,
    cobrar_pro_rata: true,
    /* notificações */
    notif_email: true,
    notif_whatsapp: false,
    email_remetente: '',
    lembrete_dias_antes: 5,
    msg_atraso: 'Sua fatura está atrasada. Regularize para manter a proteção ativa.',
    /* aparência */
    cor_primaria: '#6366f1',
    logo_url: '',
    /* segurança */
    sessao_horas: 24,
    dois_fatores: false,
}

type Settings = typeof INITIAL_SETTINGS

/* ── Componente principal ──────────────────────────────── */

export function ConfiguracoesPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [active, setActive] = useState('empresa')
    const [form, setForm] = useState<Settings>({ ...INITIAL_SETTINGS })
    const [saved, setSaved] = useState(false)

    useEffect(() => { setPageTitle('Configurações') }, [setPageTitle])

    const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
        setForm(prev => ({ ...prev, [key]: value }))

    const handleSave = () => {
        setSaved(true)
        setTimeout(() => setSaved(false), 2200)
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Configurações</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Gerencie os parâmetros do seu tenant</p>
                </div>
                <button onClick={handleSave} className="btn btn-primary btn-md gap-2">
                    {saved ? <CheckCircle2 size={18} /> : <Save size={18} />}
                    {saved ? 'Salvo!' : 'Salvar Alterações'}
                </button>
            </div>

            {/* Layout: sidebar de seções + conteúdo */}
            <div className="flex flex-col lg:flex-row gap-6">

                {/* SIDEBAR */}
                <nav className="lg:w-56 flex-shrink-0">
                    <div className="glass-card p-2 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
                        {sections.map(s => {
                            const Icon = s.icon
                            const isActive = active === s.id
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => setActive(s.id)}
                                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap
                                        ${isActive
                                            ? 'bg-primary-600 text-white shadow-md shadow-primary-600/20'
                                            : 'text-surface-700 hover:bg-surface-100'}`}
                                >
                                    <Icon size={16} />
                                    {s.label}
                                </button>
                            )
                        })}
                    </div>
                </nav>

                {/* CONTENT */}
                <div className="flex-1 glass-card p-6 lg:p-8">
                    {active === 'empresa' && (
                        <SectionWrapper title="Dados da Empresa" desc="Informações básicas de identificação e contato.">
                            <FormRow icon={Building2} label="Razão Social">
                                <input className="input" value={form.nome_empresa} onChange={e => set('nome_empresa', e.target.value)} />
                            </FormRow>
                            <FormRow icon={FileText} label="CNPJ">
                                <input className="input" value={form.cnpj} onChange={e => set('cnpj', e.target.value)} />
                            </FormRow>
                            <FormRow icon={Mail} label="E-mail de contato">
                                <input className="input" type="email" value={form.email_empresa} onChange={e => set('email_empresa', e.target.value)} />
                            </FormRow>
                            <FormRow icon={Phone} label="Telefone">
                                <input className="input" value={form.telefone_empresa} onChange={e => set('telefone_empresa', e.target.value)} />
                            </FormRow>
                            <FormRow icon={MapPin} label="Endereço">
                                <input className="input" value={form.endereco} onChange={e => set('endereco', e.target.value)} />
                            </FormRow>
                            <FormRow icon={Globe} label="Website">
                                <input className="input" type="url" value={form.site} onChange={e => set('site', e.target.value)} />
                            </FormRow>
                        </SectionWrapper>
                    )}

                    {active === 'cobranca' && (
                        <SectionWrapper title="Cobrança e Pagamentos" desc="Configurações de gateway Asaas e parâmetros financeiros.">
                            <FormRow icon={Zap} label="API Key Asaas">
                                <input className="input font-mono text-sm" value={form.asaas_api_key} onChange={e => set('asaas_api_key', e.target.value)} />
                            </FormRow>
                            <FormRow icon={Globe} label="Ambiente">
                                <select className="input" value={form.asaas_ambiente} onChange={e => set('asaas_ambiente', e.target.value as 'sandbox' | 'producao')}>
                                    <option value="sandbox">Sandbox (testes)</option>
                                    <option value="producao">Produção</option>
                                </select>
                            </FormRow>
                            <FormRow icon={CreditCard} label="Dia de vencimento padrão">
                                <input className="input w-24" type="number" min={1} max={28} value={form.dia_vencimento_padrao}
                                    onChange={e => set('dia_vencimento_padrao', +e.target.value)} />
                            </FormRow>
                            <FormRow icon={CreditCard} label="Multa por atraso (%)">
                                <input className="input w-24" type="number" step="0.5" value={form.multa_atraso}
                                    onChange={e => set('multa_atraso', +e.target.value)} />
                            </FormRow>
                            <FormRow icon={CreditCard} label="Juros mensal (%)">
                                <input className="input w-24" type="number" step="0.5" value={form.juros_mes}
                                    onChange={e => set('juros_mes', +e.target.value)} />
                            </FormRow>
                            <div className="flex items-center justify-between py-4 border-t border-surface-100">
                                <div>
                                    <p className="text-sm font-medium text-surface-900">Cobrar pro rata na adesão</p>
                                    <p className="text-xs text-surface-700/60">Calcular valor proporcional ao entrar no meio do ciclo</p>
                                </div>
                                <Toggle checked={form.cobrar_pro_rata} onChange={v => set('cobrar_pro_rata', v)} />
                            </div>
                        </SectionWrapper>
                    )}

                    {active === 'taxas' && <TaxasConfigTab />}
                    {active === 'templates' && <TemplatesConfigTab />}



                    {active === 'notificacoes' && (
                        <SectionWrapper title="Notificações" desc="Alertas e lembretes enviados automaticamente.">
                            <div className="flex items-center justify-between py-4 border-b border-surface-100">
                                <div>
                                    <p className="text-sm font-medium text-surface-900">Enviar e-mail de lembrete</p>
                                    <p className="text-xs text-surface-700/60">Notificar associados antes do vencimento</p>
                                </div>
                                <Toggle checked={form.notif_email} onChange={v => set('notif_email', v)} />
                            </div>
                            <div className="flex items-center justify-between py-4 border-b border-surface-100">
                                <div>
                                    <p className="text-sm font-medium text-surface-900">WhatsApp (em breve)</p>
                                    <p className="text-xs text-surface-700/60">Notificações via API do WhatsApp Business</p>
                                </div>
                                <Toggle checked={form.notif_whatsapp} onChange={v => set('notif_whatsapp', v)} />
                            </div>
                            <FormRow icon={Mail} label="E-mail remetente">
                                <input className="input" type="email" value={form.email_remetente} onChange={e => set('email_remetente', e.target.value)} />
                            </FormRow>
                            <FormRow icon={Bell} label="Lembrete X dias antes do vencimento">
                                <input className="input w-24" type="number" min={1} max={30} value={form.lembrete_dias_antes}
                                    onChange={e => set('lembrete_dias_antes', +e.target.value)} />
                            </FormRow>
                            <FormRow icon={Bell} label="Mensagem de atraso">
                                <textarea className="input min-h-[80px] resize-none" value={form.msg_atraso}
                                    onChange={e => set('msg_atraso', e.target.value)} />
                            </FormRow>
                        </SectionWrapper>
                    )}

                    {active === 'aparencia' && (
                        <SectionWrapper title="Aparência" desc="Personalização visual do portal do associado.">
                            <FormRow icon={Palette} label="Cor primária">
                                <div className="flex items-center gap-3">
                                    <input type="color" value={form.cor_primaria}
                                        onChange={e => set('cor_primaria', e.target.value)}
                                        className="w-10 h-10 rounded-lg cursor-pointer border border-surface-200 p-0.5"
                                    />
                                    <input className="input w-32 font-mono text-sm" value={form.cor_primaria}
                                        onChange={e => set('cor_primaria', e.target.value)} />
                                </div>
                            </FormRow>
                            <FormRow icon={Globe} label="URL do logotipo">
                                <input className="input" type="url" value={form.logo_url} placeholder="https://..."
                                    onChange={e => set('logo_url', e.target.value)} />
                            </FormRow>
                            <div className="mt-6 p-4 rounded-xl bg-surface-50 border border-surface-100">
                                <p className="text-xs font-medium text-surface-700/60 uppercase mb-3">Pré-visualização</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                                        style={{ backgroundColor: form.cor_primaria }}>
                                        PV
                                    </div>
                                    <div>
                                        <p className="font-semibold" style={{ color: form.cor_primaria }}>{form.nome_empresa}</p>
                                        <p className="text-xs text-surface-700/60">Portal do Associado</p>
                                    </div>
                                </div>
                            </div>
                        </SectionWrapper>
                    )}

                    {active === 'seguranca' && (
                        <SectionWrapper title="Segurança" desc="Controles de acesso e políticas de sessão.">
                            <FormRow icon={Shield} label="Duração da sessão (horas)">
                                <input className="input w-24" type="number" min={1} max={720} value={form.sessao_horas}
                                    onChange={e => set('sessao_horas', +e.target.value)} />
                            </FormRow>
                            <div className="flex items-center justify-between py-4 border-t border-surface-100">
                                <div>
                                    <p className="text-sm font-medium text-surface-900">Autenticação em 2 fatores</p>
                                    <p className="text-xs text-surface-700/60">Exigir verificação por e-mail ou app autenticador</p>
                                </div>
                                <Toggle checked={form.dois_fatores} onChange={v => set('dois_fatores', v)} />
                            </div>
                            <div className="mt-4 p-4 rounded-xl bg-warning-50 border border-warning-200">
                                <p className="text-sm text-warning-800 font-medium">⚠ Zona de perigo</p>
                                <p className="text-xs text-warning-700 mt-1">Ações como excluir conta ou redefinir dados estarão disponíveis em uma versão futura.</p>
                            </div>
                        </SectionWrapper>
                    )}
                </div>
            </div>
        </div>
    )
}

/* ── Sub-componentes ───────────────────────────────────── */

function SectionWrapper({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
    return (
        <div className="animate-fade-in">
            <h3 className="text-lg font-semibold text-surface-900">{title}</h3>
            <p className="text-sm text-surface-700/60 mb-6">{desc}</p>
            <div className="space-y-4">{children}</div>
        </div>
    )
}

function FormRow({ icon: Icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 py-3 border-t border-surface-100 first:border-t-0 first:pt-0">
            <label className="flex items-center gap-2 text-sm font-medium text-surface-700 min-w-[220px]">
                <Icon size={16} className="text-surface-700/40" />
                {label}
            </label>
            <div className="flex-1">{children}</div>
        </div>
    )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 flex-shrink-0
                ${checked ? 'bg-primary-600' : 'bg-surface-300'}`}
        >
            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200
                ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    )
}

/* ── Abas Extra (Fase 2 e 4) ────────────────────────────── */

function TaxasConfigTab() {
    const [taxas, setTaxas] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => { load() }, [])

    async function load() {
        setLoading(true)
        const { data } = await supabase.schema('veicular_system').from('veic_taxas_cobranca').select('*').order('tipo')
        if (data) setTaxas(data)
        setLoading(false)
    }

    async function toggleAtivo(id: string, ativo: boolean) {
        await supabase.schema('veicular_system').from('veic_taxas_cobranca').update({ ativo }).eq('id', id)
        load()
    }

    async function updateValor(id: string, campo: string, valor: string) {
        const v = parseFloat(valor.replace(',', '.')) || 0
        await supabase.schema('veicular_system').from('veic_taxas_cobranca').update({ [campo]: v }).eq('id', id)
        load()
    }

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-primary-600" /></div>

    return (
        <SectionWrapper title="Taxas de Cobrança" desc="Configure taxas percentuais ou valores fixos cobrados em cada forma de pagamento.">
            <div className="overflow-x-auto">
                <table className="table w-full text-sm">
                    <thead>
                        <tr>
                            <th>Ativo</th>
                            <th>Tipo / Label</th>
                            <th className="w-32">Tx %</th>
                            <th className="w-32">Tx Fixo (R$)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {taxas.map(t => (
                            <tr key={t.id} className={!t.ativo ? 'opacity-50' : ''}>
                                <td><Toggle checked={t.ativo} onChange={v => toggleAtivo(t.id, v)} /></td>
                                <td><strong className="block">{t.label}</strong><span className="text-xs text-surface-400">{t.tipo}</span></td>
                                <td>
                                    <input className="input max-w-[100px]" type="number" step="0.01" value={t.percentual_taxa} disabled={!t.ativo}
                                        onChange={e => {
                                            const novo = [...taxas]
                                            novo.find(x => x.id === t.id)!.percentual_taxa = e.target.value
                                            setTaxas(novo)
                                        }}
                                        onBlur={e => updateValor(t.id, 'percentual_taxa', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input className="input max-w-[100px]" type="number" step="0.01" value={t.valor_fixo} disabled={!t.ativo}
                                        onChange={e => {
                                            const novo = [...taxas]
                                            novo.find(x => x.id === t.id)!.valor_fixo = e.target.value
                                            setTaxas(novo)
                                        }}
                                        onBlur={e => updateValor(t.id, 'valor_fixo', e.target.value)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-surface-500 mt-2">Valores alterados são salvos automaticamente ao sair do campo (blur).</p>
        </SectionWrapper>
    )
}

function TemplatesConfigTab() {
    const [templates, setTemplates] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => { load() }, [])

    async function load() {
        setLoading(true)
        const { data } = await supabase.schema('veicular_system').from('veic_descricao_templates').select('*').order('tipo_cobranca')
        if (data) setTemplates(data)
        setLoading(false)
    }

    async function toggleAtivo(id: string, ativo: boolean) {
        await supabase.schema('veicular_system').from('veic_descricao_templates').update({ ativo }).eq('id', id)
        load()
    }

    async function updateTemplate(id: string, valor: string) {
        await supabase.schema('veicular_system').from('veic_descricao_templates').update({ template: valor }).eq('id', id)
        load()
    }

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-primary-600" /></div>

    return (
        <SectionWrapper title="Templates de Descrição" desc="Padrões para geração autmática da descrição de cobrança.">
            <div className="mb-4 bg-surface-50 border border-surface-200 rounded-lg p-3 text-xs text-surface-600">
                <strong>Variáveis disponíveis:</strong> {'{{placa}}'}, {'{{cpf}}'}, {'{{nome}}'}, {'{{entidade}}'}, {'{{mes}}'}, {'{{ano}}'}
            </div>
            <div className="space-y-4">
                {templates.map(t => (
                    <div key={t.id} className={`flex items-start gap-4 p-4 border rounded-xl transition-colors ${t.ativo ? 'border-primary-100 bg-surface-50' : 'border-surface-100 opacity-50'}`}>
                        <div className="pt-2">
                            <Toggle checked={t.ativo} onChange={v => toggleAtivo(t.id, v)} />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-bold text-surface-900 mb-1">{t.label} <span className="font-normal text-surface-400 font-mono text-xs">({t.tipo_cobranca})</span></label>
                            <input
                                className="input font-mono text-sm w-full"
                                value={t.template}
                                disabled={!t.ativo}
                                onChange={e => {
                                    const novo = [...templates]
                                    novo.find(x => x.id === t.id)!.template = e.target.value
                                    setTemplates(novo)
                                }}
                                onBlur={e => updateTemplate(t.id, e.target.value)}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </SectionWrapper>
    )
}
